import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import CurrencyDropdown from '@components/ringil3/Input/InputPriceWithCurrency/CurrencyDropdown.tsx';
import InputSKU from '@components/ringil3/Input/InputSKU/InputSKU.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgRoute from '@components/ringil3/icons/heading/Route.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { PriceRowForm, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import { levelPercent, newPriceLevel, newPriceRow, priceTableIssue, renumberLevels } from '@utils/contractedPriceTable.ts';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  table: PriceTableForm;
  /** Name of the Linka (or shipment) shown under the heading. */
  subjectName?: string;
  infoText?: string;
  onSave: (table: PriceTableForm) => void;
}

/** Laden factors offered for a row; 0 stands for "Výchozí" (null) because a Dropdown cannot select null. */
const MAX_LADEN_FACTOR = 40;
const DEFAULT_LF_OPTION = 0;

const formatPercent = (value: number) => `${value > 0 ? '+' : ''}${value.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} %`;

/** "2,5" or "2.5" -> 2.5; anything else (or a negative number) -> null. */
const parsePercent = (text: string): number | null => {
  const value = Number(text.trim().replace(',', '.'));
  return text.trim() !== '' && Number.isFinite(value) && value >= 0 ? value : null;
};

/**
 * "Struktura cenové tabulky": input mode (Základní / Procentuální), the levels in their order (drag the handle; names
 * follow the position around "Úroveň 0"), the % step between levels, the rows (manipulation unit + laden factor or
 * Výchozí) and the currency. Shared by all carriers; the caller reconciles the carriers' matrices with the saved structure.
 */
const PriceTableStructureSidebar: React.FC<ComponentProps> = ({ visible, onHide, table, subjectName, infoText, onSave }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const levelPrefix = tr('PriceTable.levelPrefix', 'Úroveň');
  const [value, setValue] = useState<PriceTableForm>(table);
  const [submitted, setSubmitted] = useState(false);
  const [dragIndex, setDragIndex] = useState<number>(null);
  // the drop may come before the re-render that follows dragstart, so the index is read from a ref
  const dragIndexRef = useRef<number>(null);
  const [stepText, setStepText] = useState('');

  useEffect(() => {
    if (!visible) return;
    setValue(
      renumberLevels(
        { ...table, levels: table.levels.map(l => ({ ...l })), rows: table.rows.map(r => ({ ...r })), intervals: table.intervals.map(i => ({ ...i })) },
        levelPrefix,
      ),
    );
    setSubmitted(false);
    setStepText(table.stepPercent !== null && table.stepPercent !== undefined ? String(table.stepPercent).replace('.', ',') : '');
  }, [visible]);

  const ladenFactorOptions = useMemo(
    () => [
      { label: tr('PriceTable.defaultLadenFactor', 'Výchozí'), value: DEFAULT_LF_OPTION },
      ...Array.from({ length: MAX_LADEN_FACTOR }, (_, i) => ({ label: `${i + 1}`, value: i + 1 })),
    ],
    [],
  );

  const percentage = value.inputMode === 'PERCENTAGE';
  const issue = priceTableIssue(value, tr);
  const droppedIntervals = table.intervals.filter(i => !value.levels.some(l => l.key === i.levelKey)).length;

  const update = (patch: Partial<PriceTableForm>) => setValue(v => ({ ...v, ...patch }));
  /** Every change of the levels renumbers them around the default level. */
  const updateLevels = (change: (v: PriceTableForm) => PriceTableForm) => setValue(v => renumberLevels(change(v), levelPrefix));

  const moveLevel = (from: number, to: number) => {
    if (from === to || from === null) return;
    updateLevels(v => {
      const levels = [...v.levels];
      const [moved] = levels.splice(from, 1);
      levels.splice(to, 0, moved);
      return { ...v, levels };
    });
  };

  const removeLevel = (key: string) =>
    updateLevels(v => ({ ...v, levels: v.levels.filter(l => l.key !== key), intervals: v.intervals.filter(i => i.levelKey !== key) }));

  const updateRow = (key: string, patch: Partial<PriceRowForm>) => setValue(v => ({ ...v, rows: v.rows.map(r => (r.key === key ? { ...r, ...patch } : r)) }));

  const save = () => {
    setSubmitted(true);
    if (issue) return;
    onSave(value);
    onHide();
  };

  return (
    <RingilSidebar
      visible={visible}
      heading={tr('PriceTable.structure', 'Struktura cenové tabulky')}
      onBack={onHide}
      onContinue={save}
      continueLabel={tr('PriceTable.save', 'Uložit')}
      size={'sm'}
      dataTestIdContinue={'price-structure-save'}
    >
      <div className={'flex flex-col gap-5'}>
        {subjectName && (
          <div className={'flex flex-row items-start gap-3 border-b border-solid border-content-quaternary pb-4'}>
            <SvgRoute />
            <div className={'flex flex-col'}>
              <span className={'text-sm text-content-secondary'}>{tr('PriceTable.line', 'Linka')}</span>
              <span className={'font-medium'}>{subjectName}</span>
            </div>
          </div>
        )}
        {infoText && <COInlineMessage variant={'info'}>{infoText}</COInlineMessage>}

        <div className={'flex flex-col gap-3'}>
          <div className={'font-medium'}>{tr('PriceTable.inputMode', 'Způsob zadání hodnot')}</div>
          {(['BASIC', 'PERCENTAGE'] as const).map(mode => (
            <label key={mode} className={'flex flex-row items-center gap-3 cursor-pointer'}>
              <RadioButton checked={value.inputMode === mode} onChange={() => update({ inputMode: mode })} data-testid={`price-mode-${mode}`} />
              <span>{mode === 'BASIC' ? tr('PriceTable.basic', 'Základní') : tr('PriceTable.percentage', 'Procentuální')}</span>
            </label>
          ))}
        </div>

        <div className={'flex flex-col gap-2'}>
          <div className={'font-medium'}>{tr('PriceTable.levels', 'Cenové úrovně')}</div>
          {value.levels.map((level, index) => {
            const percent = levelPercent(value, level.key);
            const isDefault = level.key === value.defaultLevelKey;
            return (
              <div
                key={level.key}
                className={clsx('flex flex-row items-center gap-2', dragIndex === index && 'opacity-50')}
                draggable
                onDragStart={e => {
                  dragIndexRef.current = index;
                  setDragIndex(index);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  moveLevel(dragIndexRef.current, index);
                  dragIndexRef.current = null;
                  setDragIndex(null);
                }}
                onDragEnd={() => {
                  dragIndexRef.current = null;
                  setDragIndex(null);
                }}
                data-testid={`price-level-row-${index}`}
              >
                <span className={'cursor-grab select-none text-content-brand font-bold px-1'} title={tr('PriceTable.dragToReorder', 'Přetažením změníte pořadí')}>
                  ⋮
                </span>
                <div
                  className={'flex-1 rounded border border-solid border-content-secondary bg-white px-3 py-2 text-sm cursor-grab'}
                  data-testid={`price-level-name-${index}`}
                >
                  {level.name}
                </div>
                {percentage && <span className={'w-16 text-right text-sm text-content-secondary'}>{percent !== null ? formatPercent(percent) : ''}</span>}
                <RingilButton
                  mode={'table'}
                  icon={<SvgDelete />}
                  tooltip={isDefault ? tr('PriceTable.defaultLevelCannotBeRemoved', 'Výchozí úroveň nelze odebrat') : tr('PriceTable.removeLevel', 'Odebrat úroveň')}
                  disabled={isDefault}
                  onClick={() => removeLevel(level.key)}
                  dataTestId={`price-level-remove-${index}`}
                />
              </div>
            );
          })}
          <div>
            <RingilButton
              mode={'text-only'}
              icon={<SvgPlus className={'w-3 h-3'} />}
              label={tr('PriceTable.addLevel', 'Přidat úroveň')}
              onClick={() => updateLevels(v => ({ ...v, levels: [...v.levels, newPriceLevel('')] }))}
              dataTestId={'price-add-level'}
            />
          </div>
          {droppedIntervals > 0 && (
            <COInlineMessage variant={'alert'}>
              {tr('PriceTable.removedLevelIntervals', 'Odebrané úrovně mají naplánovaná období ({{count}}), uložením se z plánu odstraní.', { count: droppedIntervals })}
            </COInlineMessage>
          )}
        </div>

        {percentage && (
          <div className={'grid grid-cols-2 gap-4'}>
            <Field label={tr('PriceTable.defaultLevel', 'Výchozí úroveň')} helpText={tr('PriceTable.defaultLevelHint', 'Hodnota úrovně je 0 %')}>
              <Dropdown
                value={value.defaultLevelKey}
                options={value.levels}
                optionLabel={'name'}
                optionValue={'key'}
                onChange={e => updateLevels(v => ({ ...v, defaultLevelKey: e.value }))}
                style={{ width: '100%' }}
                data-testid={'price-default-level'}
              />
            </Field>
            <Field label={tr('PriceTable.step', 'Rozdíl mezi úrovněmi')} helpText={tr('PriceTable.stepHint', 'Hodnota se vyplňuje dle řazení úrovní')}>
              <div className={'p-inputgroup'}>
                <InputText
                  value={stepText}
                  inputMode={'decimal'}
                  onChange={e => {
                    setStepText(e.target.value);
                    update({ stepPercent: parsePercent(e.target.value) });
                  }}
                  data-testid={'price-step'}
                />
                <span className={'p-inputgroup-addon'}>%</span>
              </div>
            </Field>
          </div>
        )}

        <div className={'flex flex-col gap-2'}>
          <div className={'font-medium'}>{tr('PriceTable.rows', 'Manipulační jednotky a ladenfaktory')}</div>
          {value.rows.length > 0 && (
            <div className={'grid grid-cols-[2fr_1fr_auto] gap-2 text-xs text-content-secondary'}>
              <span>{tr('PriceTable.rowMu', 'Manipulační jednotka')}</span>
              <span>{tr('PriceTable.ladenFactors', 'Ladenfaktor')}</span>
              <span className={'w-8'} />
            </div>
          )}
          {value.rows.map((row, index) => (
            <div key={row.key} className={'grid grid-cols-[2fr_1fr_auto] gap-2 items-center'} data-testid={`price-row-${index}`}>
              <InputSKU
                value={row.cargoTemplateCode}
                companyId={loggedUser?.companyId}
                fullWidth
                onChange={code => updateRow(row.key, { cargoTemplateCode: code })}
                onChangeCargoTemplate={ct => updateRow(row.key, { cargoTemplateCode: ct.code, cargoTemplateName: ct.name })}
                dataTestId={`price-row-mu-${index}`}
              />
              <Dropdown
                value={row.ladenFactor ?? DEFAULT_LF_OPTION}
                options={ladenFactorOptions}
                optionLabel={'label'}
                optionValue={'value'}
                style={{ width: '100%' }}
                onChange={e => updateRow(row.key, { ladenFactor: e.value === DEFAULT_LF_OPTION ? null : e.value })}
                data-testid={`price-row-lf-${index}`}
              />
              <RingilButton
                mode={'table'}
                icon={<SvgDelete />}
                tooltip={tr('PriceTable.removeRow', 'Odebrat řádek')}
                onClick={() => setValue(v => ({ ...v, rows: v.rows.filter(r => r.key !== row.key) }))}
              />
            </div>
          ))}
          <div>
            <RingilButton
              mode={'text-only'}
              icon={<SvgPlus className={'w-3 h-3'} />}
              label={tr('PriceTable.addRow', 'Přidat řádek')}
              onClick={() => setValue(v => ({ ...v, rows: [...v.rows, newPriceRow(v.rows[v.rows.length - 1]?.cargoTemplateCode ?? null)] }))}
              dataTestId={'price-add-row'}
            />
          </div>
        </div>

        <Field label={tr('PriceTable.currency', 'Měna')} required>
          <CurrencyDropdown selectedCurrency={value.currency} onSelected={currency => update({ currency })} companyId={loggedUser?.companyId} fullWidth />
        </Field>

        {submitted && issue && <COInlineMessage variant={'alert'}>{issue}</COInlineMessage>}
      </div>
    </RingilSidebar>
  );
};

export default PriceTableStructureSidebar;
